import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { LeadBoard } from "./board/leadboard";
// import { Rewards } from "./rewards/rewards";
import { Wallet } from "./wallet/wallet";
import { useSearchParams } from "react-router-dom";

const tabContentVariants = {
  initial: {
    y: 10,
    opacity: 0,
  },
  enter: {
    y: 0,
    opacity: 1,
  },
  exit: {
    y: -10,
    opacity: 0,
  },
};

function WorldAchievementUI({ primaryColor = "#FFD700" }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = [
    {
      name: "leaderboard",
      label: "LeaderBoard",
      render: () => <LeadBoard primaryColor={primaryColor} />,
    },
    {
      name: "rewards",
      label: "Rewards",
      render: () => (
        <div className="flex flex-col items-center justify-center py-16 min-h-[500px] px-4 relative overflow-hidden">
          {/* Animated background elements */}
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.2 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <div className="w-32 h-32 bg-yellow-500 rounded-full absolute top-10 left-10"></div>
            <div className="w-24 h-24 bg-purple-500 rounded-full absolute bottom-20 right-8"></div>
            <div className="w-20 h-20 bg-pink-500 rounded-full absolute top-1/3 right-16"></div>
          </motion.div>

          <div className="text-center relative z-10">
            <motion.div className="text-6xl mb-6">🎉🏆🎊</motion.div>

            <motion.h2
              className="text-3xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 bg-clip-text text-transparent mb-3 drop-shadow-lg"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Winners & Prizes Announcement
            </motion.h2>

            <motion.p
              className="text-white text-lg font-medium mb-4 px-4"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Winners and prizes are announced every Sunday!
            </motion.p>

            <motion.p
              className="text-[#FFFFFFBF] text-sm font-medium mb-8 px-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              📝 Make sure your profile is updated so your name appears
              correctly on the leaderboard.
            </motion.p>

            <div className="flex justify-center space-x-4 text-4xl mb-6">
              <motion.span
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
              >
                🎁
              </motion.span>
              <motion.span
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
              >
                💰
              </motion.span>
            </div>

            <motion.div className="absolute -top-4 left-1/4 text-2xl">
              <motion.span
                animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                ⭐
              </motion.span>
            </motion.div>
            <motion.div className="absolute top-8 right-1/4 text-2xl">
              <motion.span
                animate={{ scale: [1, 1.8, 1], rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                🎈
              </motion.span>
            </motion.div>
          </div>
        </div>
      ),
    },
    {
      name: "wallet",
      label: "Wallet",
      render: () => <Wallet primaryColor={primaryColor} />,
    },
  ];

  // Helper function to get tab from search params
  const getTabFromParams = () => {
    const tabParam = searchParams.get("tab");
    const foundTab = tabs.find((tab) => tab.name === tabParam);
    return foundTab || tabs[0];
  };

  const [activeTab, setActiveTab] = useState(getTabFromParams);

  // Sync activeTab with URL changes (handles browser back/forward)
  useEffect(() => {
    const tabFromUrl = getTabFromParams();
    if (tabFromUrl.name !== activeTab.name) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Set default tab in URL if no tab query exists
  useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: tabs[0].name });
    }
  }, []);

  const handleClick = (e, tab) => {
    e.preventDefault();
    setActiveTab(tab);
    setSearchParams({ tab: tab.name });
  };

  const isSelected = (tab) => activeTab.name === tab.name;

  return (
    <div>
      <div className="px-4">
        <div className="rmc-tabHeader overflow-x-scroll hidden-scrollbar">
          {tabs.map((tab) => (
            <div
              key={tab.name}
              className={[
                "rmc-tabItem cursor-pointer",
                isSelected(tab) ? "rmc-selected" : "",
              ].join(" ")}
            >
              <span onClick={(e) => handleClick(e, tab)}>{tab.label}</span>

              {isSelected(tab) && (
                <motion.div
                  layoutId="indicator"
                  className={"rmc-indicator"}
                  style={{ backgroundColor: primaryColor }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className={"rmc-tabContent "}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab.name || "empty"}
            variants={tabContentVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {activeTab && activeTab?.render()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default WorldAchievementUI;
